import { Box, Button, CardContent, Chip, Divider, Typography, useTheme } from "@mui/material";
import { Circle } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import useTimeFormat from "../../../../hooks/useTimeFormat/useTimeFormat";
import useServiceTypeColor from "../hooks/useServiceTypeColor";
import { OrderRequestData } from "../Domain/RemoteOrderRequests.types";
import {
  ServiceDeliveryStatus,
  ServiceType
} from "../../../../features/Booking-Form/typings/booking-service-administration.types";
import { t, TFunction } from "i18next";
import { BookingRequestsStatus } from "../../../BookingRequest/typings/booking-requests.types";
import { capitalizeFirstAndLower } from "../../../FacilityManager/Components/Toolbars/InfoSidebar/PermissionSelect/PermissionSelect.functions";

type OrderRequestCardItemProps = {
  item: OrderRequestData;
  isSelected: boolean;
  filterByType: ServiceType[];
  isDetailLoading: boolean;
  isArchive: boolean;
  isBookingCancelled: boolean;
  handleOpenDetail?: (bookingId: string) => void;
};

export function OrderRequestCardItem({
  item,
  isSelected,
  filterByType,
  isDetailLoading,
  isArchive,
  isBookingCancelled,
  handleOpenDetail
}: Readonly<OrderRequestCardItemProps>) {
  const { t } = useTranslation();
  const theme = useTheme();

  const { dateFormat, timeFormat } = useTimeFormat();
  const { cateringColor, equipmentColor, layoutColor } = useServiceTypeColor();

  const commonChipStyle = {
    mx: 0,
    my: 0.2,
    width: "100%",
    justifyContent: "flex-start",
    pl: 0.5,
    color: "black"
  };

  const orderDate = DateTime.fromISO(item.date).toFormat(dateFormat);
  const orderTimeFrom = DateTime.fromISO(item.timeFrom).toFormat(timeFormat);
  const orderTimeTo = DateTime.fromISO(item.timeTo).toFormat(timeFormat);

  const getChipFontWeight = (type: ServiceType) => (filterByType.includes(type) ? 600 : 300);

  return (
    <CardContent
      sx={{
        p: 2,
        "&:last-child": { pb: 2 },
        backgroundColor: theme.palette.background.default,
        background: theme.palette.background.default,
        backgroundImage: "none"
      }}
      data-testid={`droppable-listBox-${item.id}-contents`}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 600 }}>{orderDate}</Typography>
        {isSelected && (
          <StatusInfo
            item={item}
            isArchive={isArchive}
            isBookingCancelled={isBookingCancelled}
            deliveryStatus={item.deliveryStatus}
            t={t}
          />
        )}
      </Box>

      <Divider sx={{ opacity: "0.6" }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>{orderTimeFrom}</Typography>
          {`-`}
          <Typography>{orderTimeTo}</Typography>
        </Box>
        {isSelected && (
          <Button
            size="small"
            data-testid="open-detail-btn"
            loading={isDetailLoading}
            variant={"contained"}
            onClick={() => handleOpenDetail?.(item.bookingId)}
          >
            {t("Details")}
          </Button>
        )}
      </Box>
      <Typography sx={{ fontWeight: 600 }}>{item.inventoryName}</Typography>

      <Box sx={{ display: "flex" }}>
        <Typography>{item.locationName}</Typography>
        {`,`}
        <Typography sx={{ pl: 0.5 }}>{item.floorName}</Typography>
      </Box>

      <Box sx={{ mt: 0.5 }}>
        {item.numberOfCaterings > 0 && (
          <Chip
            label={t("OrderRequest_Catering", { number: item.numberOfCaterings })}
            sx={{
              ...commonChipStyle,
              backgroundColor: cateringColor,
              fontWeight: getChipFontWeight(ServiceType.Catering)
            }}
          />
        )}
        {item.numberOfBookableEquipments > 0 && (
          <Chip
            label={t("OrderRequest_Equipment", { number: item.numberOfBookableEquipments })}
            sx={{
              ...commonChipStyle,
              backgroundColor: equipmentColor,
              fontWeight: getChipFontWeight(ServiceType.Bookable_Equipment)
            }}
          />
        )}
        {item.numberOfRoomLayouts > 0 && (
          <Chip
            label={t("OrderRequest_Layout", { number: item.numberOfRoomLayouts })}
            sx={{
              ...commonChipStyle,
              backgroundColor: layoutColor,
              fontWeight: getChipFontWeight(ServiceType.Room_Layout)
            }}
          />
        )}
      </Box>

      <Divider sx={{ my: 0.5, opacity: "0.6" }} />

      <Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Typography variant={"body2"}> {t("OrderList_OrderedBy")}</Typography>
          <Typography variant={"body2"}>{item.requestedByUser}</Typography>
        </Box>

        {item.status === BookingRequestsStatus.CANCELLED && (
          <Typography variant={"body2"} sx={{ mt: 0.5 }}>
            {formatCancelledByUser(item, t)}
          </Typography>
        )}
      </Box>
    </CardContent>
  );
}
export const statusCircleColor = (
  isArchive: boolean,
  isBookingCancelled: boolean,
  deliveryStatus: ServiceDeliveryStatus
) => {
  if (isArchive || isBookingCancelled) {
    return "grey";
  }
  switch (deliveryStatus) {
    case ServiceDeliveryStatus.ORDERED:
      return "red";
    case ServiceDeliveryStatus.IN_PROGRESS:
      return "yellow";
    case ServiceDeliveryStatus.READY:
      return "green";
    default:
      return "blue";
  }
};

export const cancelledByActorName = (item: OrderRequestData): string =>
  item.cancelledByUser?.trim() || item.requestedByUser;

export const formatCancelledByUser = (item: OrderRequestData, t: TFunction): string =>
  t("_orderRequest_cancelledByUser", { name: cancelledByActorName(item) });

const statusInfoLabel = (
  item: OrderRequestData,
  isArchive: boolean,
  isBookingCancelled: boolean,
  t: TFunction
): string => {
  if (isBookingCancelled) return capitalizeFirstAndLower(t(BookingRequestsStatus.CANCELLED));
  if (isArchive) return t("Archive");
  return t(item.deliveryStatus);
};

export const StatusInfo = ({
  item,
  isArchive,
  isBookingCancelled,
  deliveryStatus,
  t
}: {
  item: OrderRequestData;
  isArchive: boolean;
  isBookingCancelled: boolean;
  deliveryStatus: ServiceDeliveryStatus;
  t: TFunction;
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Circle
        sx={{
          fontSize: "8px",
          color: statusCircleColor(isArchive, isBookingCancelled, deliveryStatus)
        }}
      />
      <Typography>{statusInfoLabel(item, isArchive, isBookingCancelled, t)}</Typography>
    </Box>
  );
};

/** Admin-selectable line statuses only — CANCELLED is never an option (set by other flows). */
export const EditBookingRequestsStatus = {
  APPROVED: BookingRequestsStatus.APPROVED,
  REJECTED: BookingRequestsStatus.REJECTED,
  REQUESTED: BookingRequestsStatus.REQUESTED
} as const;

export type ServiceLineStatusSelectOption = {
  label: string;
  value: BookingRequestsStatus;
  key: string;
};

export const toServiceLineStatusOption = (
  stat: BookingRequestsStatus,
  translate: TFunction,
  index = 0
): ServiceLineStatusSelectOption => ({
  label: capitalizeFirstAndLower(translate(stat)),
  value: stat,
  key: `${stat}${index}`
});

/** MRT `editSelectOptions` — same shape as {@link serviceLineStatusDisplayOption}. */
export const statusEditSelectOpts = (): ServiceLineStatusSelectOption[] =>
  Object.values(EditBookingRequestsStatus).map((stat, index) =>
    toServiceLineStatusOption(stat, t, index)
  );

/** Read-only row status (e.g. CANCELLED) — same object shape as select options. */
export const serviceLineStatusDisplayOption = (
  status: BookingRequestsStatus | undefined,
  translate: TFunction
): ServiceLineStatusSelectOption | null => {
  if (status === undefined) {
    return null;
  }
  const resolved = translate(status);
  const text = typeof resolved === "string" && resolved.length > 0 ? resolved : status;
  return {
    label: capitalizeFirstAndLower(text),
    value: status,
    key: `${status}-row`
  };
};

export const serviceLineStatusDisplayLabel = (
  status: BookingRequestsStatus | undefined,
  translate: TFunction
): string => serviceLineStatusDisplayOption(status, translate)?.label ?? "";
